import { isAxiosError } from 'axios';
import { isImmutable } from 'immutable';

import { toSentenceCase } from '@/utils/text';

const GENERAL_ERROR_KEYS = ['detail', 'non_field_errors', '__all__'];

const humanizeKey = (key: string) => toSentenceCase(key.replace(/_/g, ' '));

const withPrefix = (prefix: string, message: string) =>
  prefix ? `${prefix}: ${message}` : message;

export const flattenApiErrors = (data: unknown, prefix = ''): string[] => {
  if (data === null || data === undefined || data === '') return [];

  if (isImmutable(data)) return flattenApiErrors(data.toJS(), prefix);

  if (typeof data === 'string') return [withPrefix(prefix, data)];

  if (typeof data === 'number' || typeof data === 'boolean')
    return [withPrefix(prefix, String(data))];

  if (Array.isArray(data))
    return data.flatMap((item) => flattenApiErrors(item, prefix));

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;

    if (record.api === true && 'errors' in record)
      return flattenApiErrors(record.errors, prefix);

    if (typeof record.message === 'string' && 'code' in record)
      return [withPrefix(prefix, record.message)];

    return Object.entries(record).flatMap(([key, value]) =>
      flattenApiErrors(
        value,
        GENERAL_ERROR_KEYS.includes(key)
          ? prefix
          : withPrefix(prefix, humanizeKey(key)),
      ),
    );
  }

  return [];
};

export const getFieldErrors = (data: unknown): Record<string, string[]> => {
  const source = isImmutable(data) ? data.toJS() : data;
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};

  return Object.fromEntries(
    Object.entries(source as Record<string, unknown>)
      .map(([key, value]) => [key, flattenApiErrors(value)] as const)
      .filter(([, messages]) => messages.length > 0),
  );
};

const STATUS_MESSAGES: Record<number, string> = {
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested item could not be found.',
  405: 'This action is not supported.',
  413: 'The file is too large to upload.',
};

export const getApiErrorMessages = (
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string[] => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const messages = flattenApiErrors(error.response?.data);
    if (messages.length && typeof error.response?.data !== 'string')
      return messages;
    if (status && STATUS_MESSAGES[status]) return [STATUS_MESSAGES[status]];
    if (!error.response)
      return [
        error.code === 'ECONNABORTED'
          ? 'The request timed out. Please try again.'
          : 'Could not reach the server. Please check your connection.',
      ];
    return [fallback];
  }

  const messages = flattenApiErrors(error);
  if (messages.length) return messages;

  if (error instanceof Error && error.message) return [error.message];

  return [fallback];
};

export const getApiErrorMessage = (error: unknown, fallback?: string) =>
  getApiErrorMessages(error, fallback).join(' ');
